import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import Modal, { ModalBody, ModalFooter } from '@components/common/Modal';
import Button from '@components/common/Button';
import UserForm from '@components/users/UserForm';
import { userService } from '@services/userService';

const UserEntryModal = ({
  isOpen,
  onClose,
  user = null,
  mode = 'create' // 'create' | 'edit'
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const queryClient = useQueryClient();

  // Reset submitting state when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setIsSubmitting(false);
    }
  }, [isOpen]);

  // Create user mutation
  const createUserMutation = useMutation({
    mutationFn: async (userData) => {
      const response = await userService.createUser(userData);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(['users']);
      toast.success('User created successfully');
      onClose();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to create user');
    },
    onSettled: () => {
      setIsSubmitting(false);
    }
  });

  // Update user mutation
  const updateUserMutation = useMutation({
    mutationFn: async (userData) => {
      const response = await userService.updateUser(user.id, userData);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(['users']);
      queryClient.invalidateQueries(['user', user.id]);
      toast.success('User updated successfully');
      onClose();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to update user');
    },
    onSettled: () => {
      setIsSubmitting(false);
    }
  });

  const handleSubmit = async (userData) => {
    setIsSubmitting(true);
    
    try {
      if (mode === 'create') {
        await createUserMutation.mutateAsync(userData);
      } else {
        await updateUserMutation.mutateAsync(userData);
      }
    } catch (error) {
      // Error handling is done in the mutations
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  const title = mode === 'create' ? 'Add New User' : `Edit User - ${user?.first_name} ${user?.last_name}`;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCancel}
      size="2xl"
      title={title}
      closeOnOverlayClick={!isSubmitting}
      closeOnEscape={!isSubmitting}
      showCloseButton={!isSubmitting}
    >
      <ModalBody style={{ padding: 0 }}>
        <UserForm
          user={user}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          loading={isSubmitting}
          mode={mode}
        />
      </ModalBody>
    </Modal>
  );
};

export default UserEntryModal;